import { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import ResponsiveModal from '@/comp/Forms/common/ResponsiveModal';
import { useStores } from '@/hooks/use-stores';
import { useStage } from '../../Provider';
import { Form, Input, message, Select, SelectProps } from 'antd';

const AddBlockReasonModal = observer(() => {
  const { t } = useTranslation();
  const [isBlockReasonOther, setIsBlockReasonOther] = useState(false);
  const { lifecycleStore } = useStores();
  const { blockReasons = [], blocks = [] } = useStage();
  const [canSave, setCanSave] = useState(false);
  const { isBlockedReasonModalOpen, setIsBlockedReasonModalOpen } = lifecycleStore;
  const [form] = Form.useForm();

  const activeBlockedReasonIds = new Set(blocks.filter(({ endAt }) => !endAt).map(block => block.reason.code));
  const availableBlockReasons: SelectProps['options'] = blockReasons
    .filter(reason => reason.code === 'OTHER' || !activeBlockedReasonIds.has(reason.code))
    .map(reason => ({
      code: reason.code,
      value: reason.name,
      disabled: !reason.active,
      label: t(reason.name),
    }))
    .sort((a, b) => {
      if (a.code === 'OTHER') return 1;
      if (b.code === 'OTHER') return -1;
      return a.label.localeCompare(b.label);
    });

  const handleClose = () => {
    form.resetFields();
    setIsBlockReasonOther(false);
    setIsBlockedReasonModalOpen(false);
  };

  const handleSave = useCallback(async () => {
    message.loading({
      content: t('Blocking stage...'),
      key: 'block-reason-modal',
      duration: 15,
    });
    const reasonName = form.getFieldValue('reason');
    const otherReason = form.getFieldValue('reason-free-text');
    const reason = blockReasons.find(reason => reason.name === reasonName);
    const isBlocked = await lifecycleStore.blockStage(reason, otherReason);
    if (isBlocked) {
      message.success({
        content: t('Stage blocked successfully'),
        key: 'block-reason-modal',
      });
      handleClose();
    } else {
      message.error({
        content: t('Failed to block stage'),
        key: 'block-reason-modal',
      });
    }
  }, [blockReasons]);

  const handleValuesChange = (_, allFields: Record<string, string>) => {
    const isOther = allFields['reason'] === 'Other';
    const canSave = allFields['reason'] && (!isOther || !!allFields['reason-free-text']);
    setIsBlockReasonOther(isOther);
    setCanSave(canSave);
  };

  useEffect(() => {
    if (isBlockedReasonModalOpen) {
      form.resetFields();
      if (blockReasons.length === 0) {
        lifecycleStore.getBlockReasons();
      }
    }
  }, [isBlockedReasonModalOpen]);

  return (
    <ResponsiveModal
      visible={isBlockedReasonModalOpen}
      title={t('blockStageModalTitle')}
      onClose={handleClose}
      onSave={handleSave}
      disableSaveButton={!canSave}
      saveButtonText={t('Save reason')}
    >
      <div>{t('blockStageModalDescription')}</div>
      <Form form={form} name="block-reason-form" layout="vertical" onValuesChange={handleValuesChange} preserve={false}>
        <Form.Item name="reason" label={t('blockStageModalReason')}>
          <Select placeholder={t('Select')} options={availableBlockReasons} loading={blockReasons.length === 0} />
        </Form.Item>
        {isBlockReasonOther && (
          <Form.Item name="reason-free-text" label={t('Other')}>
            <Input.TextArea maxLength={100} showCount />
          </Form.Item>
        )}
      </Form>
    </ResponsiveModal>
  );
});

export default AddBlockReasonModal;
